/**
 * Per embed-streaming-tts-chunks spec — splits an LLM reply at natural
 * sentence boundaries so each chunk can be synthesised independently
 * + played back-to-back. Lets the avatar start talking ~1s after the
 * reply lands instead of waiting for the full TTS render.
 */

const SENTENCE_TERMINATORS = new Set([
  '。', '？', '?', '！', '!', '.', '；', ';',
]);

export interface SplitForTtsOptions {
  /**
   * Buffer must be at least this many chars (including the terminator)
   * before a split fires. Prevents 1-char chunks like '。' or '?'
   * standing alone. Default 5.
   */
  minChunkChars?: number;
  /**
   * Hard cap on chunk count per turn — prevents fan-out floods when an
   * LLM produces a pathological 50-sentence reply. The N-th chunk
   * absorbs the remaining tail. Default 12.
   */
  maxChunks?: number;
  /**
   * Per voice-loop iter 14 — when true, keep chunks[0] as a single
   * sentence (low TTFA) but merge every consecutive pair from
   * chunks[1..] so later round-trips synthesize longer payloads.
   * Net effect: TTFA stays the same, total round-trips drop ~50%
   * past the first chunk. Default false (caller opts in via the
   * `adaptive_tts_chunks` flag).
   */
  adaptiveChunkSize?: boolean;
}

/**
 * Walk the input character-by-character, emitting a chunk every time a
 * sentence terminator is hit AND the accumulated buffer is ≥ min size.
 * Trailing fragments without a terminator are merged into the previous
 * chunk (no orphan stubs). Bounded by `maxChunks` to keep the synth
 * fan-out reasonable.
 */
export function splitForTts(text: string, opts: SplitForTtsOptions = {}): string[] {
  const minChunkChars = opts.minChunkChars ?? 5;
  const maxChunks = opts.maxChunks ?? 12;

  const chunks: string[] = [];
  let buf = '';

  for (const ch of text) {
    buf += ch;
    if (SENTENCE_TERMINATORS.has(ch) && buf.length >= minChunkChars) {
      chunks.push(buf);
      buf = '';
    }
  }

  if (buf.length > 0) {
    if (buf.length >= minChunkChars || chunks.length === 0) {
      chunks.push(buf);
    } else {
      // Trailing tail is too short to stand alone AND we have a
      // previous chunk — merge it in so playback doesn't get a
      // tiny stub at the end.
      chunks[chunks.length - 1] += buf;
    }
  }

  // Hard cap — collapse the tail into the maxChunks-th entry.
  let result = chunks;
  if (result.length > maxChunks) {
    const head = result.slice(0, maxChunks - 1);
    const tail = result.slice(maxChunks - 1).join('');
    result = [...head, tail];
  }

  // voice-loop iter 14 — adaptive: chunks[0] stays solo (preserves
  // TTFA), then merge pairs from index 1 onward. Halves the synth
  // round-trips past the first chunk without changing first-audio
  // latency.
  if (opts.adaptiveChunkSize && result.length >= 3) {
    const merged: string[] = [result[0]];
    for (let i = 1; i < result.length; i += 2) {
      if (i + 1 < result.length) {
        merged.push(result[i] + result[i + 1]);
      } else {
        merged.push(result[i]);
      }
    }
    return merged;
  }

  return result;
}
